import Link from 'next/link'
import { MapPin, ArrowRight } from 'lucide-react'
import type { CityCardView } from '@/data/city-seo/card-view'
import { SEASON } from './seasons'
import { CITY_ICONS, DefaultCityIcon } from '@/components/icons/city-icons'

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-min flex-[1_1_7rem]">
      <span className="mb-1 block text-xs font-bold uppercase tracking-[0.05em] text-[#6b7a73]">
        {label}
      </span>
      <strong className="text-[0.95rem] font-semibold text-[#14241b]">{value}</strong>
    </div>
  )
}

export function CityHubCard({
  city,
  headingLevel = 'h3',
}: {
  city: CityCardView
  headingLevel?: 'h2' | 'h3'
}) {
  const a = SEASON[city.season]
  const SeasonIcon = a.Icon
  const CityIcon = CITY_ICONS[city.slug] ?? DefaultCityIcon
  const Heading = headingLevel

  return (
    <Link
      href={city.href}
      prefetch={false}
      className="ds-glass group relative flex flex-col gap-[18px] overflow-hidden rounded-3xl p-[26px] no-underline transition duration-300 ease-out hover:-translate-y-[5px]"
    >
      {/* Seasonal corner glow -- inline position/z-index so it sits UNDER the
          ds-glass ::before sheen (z-1) and the content (z-2). */}
      <div
        aria-hidden
        className="pointer-events-none -right-12 -top-12 h-[200px] w-[200px] rounded-full"
        style={{
          position: 'absolute',
          zIndex: 0,
          background: `radial-gradient(circle, ${a.glow} 0%, transparent 68%)`,
        }}
      />

      <div className="flex flex-wrap items-start justify-between gap-3.5">
        <div className="flex min-w-0 flex-[1_1_12rem] flex-wrap items-start gap-3.5">
          <span
            aria-hidden
            className="flex shrink-0 items-center justify-center"
            style={{ width: 44, height: 44, borderRadius: 13, background: a.bg, boxShadow: `inset 0 0 0 1px ${a.accent}2e` }}
          >
            <CityIcon color={a.accent} size={26} />
          </span>
          <div className="min-w-0 flex-[1_1_min-content]">
            <Heading className="m-0 break-words text-[1.55rem] font-extrabold tracking-[-0.025em] text-[#14241b]">
              {city.city}
            </Heading>
            <span className="mt-1.5 inline-flex items-center gap-1.5 text-[0.9rem] text-[#6b7a73]">
              <MapPin size={15} className="shrink-0" style={{ color: a.accent }} />
              {city.state} · {city.region}
            </span>
          </div>
        </div>
        <span
          className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-white/65 px-3 py-1.5 text-[0.76rem] font-bold"
          style={{ color: a.accent700, background: a.bg }}
        >
          <SeasonIcon size={13} />
          {city.seasonLabel}
        </span>
      </div>

      <div className="flex flex-wrap gap-4 border-y border-[rgba(20,45,30,0.10)] py-[17px]">
        <Fact label="USDA zone" value={city.zoneShort} />
        <Fact label="Best grass" value={city.grassShort} />
        <Fact label="Climate" value={city.climateLabel} />
      </div>

      <p className="m-0 text-[0.97rem] leading-relaxed text-[#41514a]">{city.hook}</p>

      <span
        className="mt-auto inline-flex items-center gap-1.5 text-[0.94rem] font-semibold"
        style={{ color: a.accent700 }}
      >
        View the {city.city} guide
        <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
      </span>
    </Link>
  )
}
